/**
 * LaGift — header behaviour
 * ---------------------------------------------------------------------------
 * Written for this theme. Prestige's design is the reference; none of its code
 * is used (its runtime is a single compiled bundle). Everything here is vanilla
 * custom elements in Dawn's style: no framework, no build step.
 *
 * Three elements:
 *   <lagift-header>          sticky behaviour, hide-on-scroll, height variable
 *   <lagift-nav-disclosure>  one dropdown or mega menu
 *   <lagift-menu-drawer>     the mobile drawer
 *
 * Everything degrades: the nav is real links, the search panel is a real form
 * that submits to /search, and the drawer's panels are plain nested lists. If
 * this file never loads the header is still fully usable.
 */
/**
 * Scoped so the file can run more than once.
 *
 * A `<script src>` executes once per tag, not once per URL. Several sections
 * include this, and a merchant can add the same section twice, so on a busy
 * page it runs repeatedly - and a top-level class or const throws
 * "Identifier ... has already been declared" on every run after the first.
 * Inside a function there is nothing shared to collide with.
 */
(() => {
  const MOBILE_QUERY = '(max-width: 989px)';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ==========================================================================
     <lagift-header>
     ========================================================================== */

  class LagiftHeader extends HTMLElement {
    connectedCallback() {
      this.lastScroll = window.scrollY;
      this.hideOnScroll = this.hasAttribute('data-hide-on-scroll');

      // Publish the header's height so sticky offsets and dropdown max-heights
      // can be expressed in CSS rather than recalculated in script.
      this.resizeObserver = new ResizeObserver(() => this.publishHeight());
      this.resizeObserver.observe(this);
      this.publishHeight();

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.searchToggle = this.querySelector('.lagift-header__search-toggle');
      this.drawerToggle = this.querySelector('.lagift-header__drawer-toggle');

      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', () => this.toggleSearch());
      }

      if (this.drawerToggle) {
        this.drawerToggle.addEventListener('click', () => {
          document.getElementById('lagift-menu-drawer')?.open(this.drawerToggle);
        });
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    publishHeight() {
      document.documentElement.style.setProperty('--lg-header-height', `${this.offsetHeight}px`);
    }

    onScroll() {
      const current = window.scrollY;

      // Past the header's own height, it is no longer over the hero, so the
      // transparent treatment has to give way to a readable solid bar.
      this.classList.toggle('is-scrolled', current > this.offsetHeight);

      if (this.hideOnScroll) {
        const scrollingDown = current > this.lastScroll;
        const pastHeader = current > this.offsetHeight;
        // Never hide the header while a menu or the search panel is open, or the
        // thing the customer is looking at slides off the screen under them.
        const isBusy = this.querySelector('[aria-expanded="true"]') !== null;

        this.classList.toggle('is-hidden', scrollingDown && pastHeader && !isBusy);
      }

      this.lastScroll = current;
    }

    toggleSearch() {
      const panel = document.getElementById('lagift-header-search');
      if (!panel) return;

      const overlay = document.querySelector('[data-search-overlay]');

      if (panel.classList.contains('is-open')) {
        this.closeSearch(panel, overlay);
      } else {
        this.openSearch(panel, overlay);
      }
    }

    /**
     * Bring the header back into view.
     *
     * Named to match Dawn's own `sticky-header`, because Dawn's cart
     * notification calls `header.reveal()` before it opens - otherwise the panel
     * appears hanging below a header that has scrolled away.
     */
    reveal() {
      this.classList.remove('is-hidden');
    }

    /**
     * Open and closed are a single class; the panel's slide, its visibility and
     * the overlay's fade are all handled in CSS. No timers, no transitionend, so
     * there is no state that can get stuck half-open.
     */
    openSearch(panel, overlay) {
      panel.classList.add('is-open');
      overlay?.classList.add('is-open');
      this.searchToggle.setAttribute('aria-expanded', 'true');

      // A hidden header would drag the panel off screen with it.
      this.classList.remove('is-hidden');

      // Drop the transparent treatment while the panel is open, so the header and
      // the solid panel below it read as one surface. The panel is a sibling of
      // the header rather than a descendant, so :focus-within cannot cover this.
      this.classList.add('is-search-open');

      panel.querySelector('input[type="search"]')?.focus();

      // The close button gets its own listener rather than relying on the
      // document-level one below. It is the primary way out of the panel, so it
      // should not depend on anything to do with event delegation or ordering.
      if (!this.searchCloseBound) {
        panel.querySelector('[data-search-close]')?.addEventListener('click', () => {
          this.closeSearch(panel, overlay);
        });
        this.searchCloseBound = true;
      }

      // Outside clicks and Escape.
      this.dismissSearch = (event) => {
        const insidePanel = panel.contains(event.target);
        const onToggle = this.searchToggle.contains(event.target);
        const pressedEscape = event.key === 'Escape';

        if ((!insidePanel && !onToggle) || pressedEscape) {
          this.closeSearch(panel, overlay);
        }
      };

      // Deferred, so the click that opened the panel does not immediately close it.
      requestAnimationFrame(() => {
        document.addEventListener('click', this.dismissSearch);
        document.addEventListener('keydown', this.dismissSearch);
      });
    }

    closeSearch(panel, overlay) {
      panel.classList.remove('is-open');
      overlay?.classList.remove('is-open');
      this.classList.remove('is-search-open');
      this.searchToggle.setAttribute('aria-expanded', 'false');

      document.removeEventListener('click', this.dismissSearch);
      document.removeEventListener('keydown', this.dismissSearch);

      // preventScroll, or returning focus to the header can yank the page.
      this.searchToggle.focus({ preventScroll: true });
    }
  }

  customElements.define('lagift-header', LagiftHeader);

  /* ==========================================================================
     <lagift-nav-disclosure>
     ========================================================================== */

  /**
   * One dropdown or mega menu.
   *
   * Opens on hover for mice and on click for touch and keyboards, which is the
   * only combination that works for all three. A hover-only menu is unusable on a
   * phone; a click-only menu feels sluggish on a desktop.
   *
   * Closing on hover is delayed slightly, so a diagonal path from the trigger to
   * the panel does not dismiss the menu halfway.
   */
  class LagiftNavDisclosure extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[aria-expanded]');
      this.panel = this.querySelector('[data-nav-panel]');
      if (!this.toggle || !this.panel) return;

      this.closeTimer = null;
      this.mobile = window.matchMedia(MOBILE_QUERY);

      this.toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.setOpen(!this.isOpen);
      });

      this.addEventListener('mouseenter', () => {
        if (this.mobile.matches) return;
        clearTimeout(this.closeTimer);
        this.setOpen(true);
      });

      this.addEventListener('mouseleave', () => {
        if (this.mobile.matches) return;
        this.closeTimer = setTimeout(() => this.setOpen(false), 120);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.setOpen(false);
          this.toggle.focus();
        }
      });

      // Tabbing out of the menu entirely should close it.
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setOpen(false);
      });
    }

    get isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    setOpen(open) {
      if (open === this.isOpen) return;

      if (open) {
        // Only one menu at a time, or two panels overlap.
        document.querySelectorAll('lagift-nav-disclosure').forEach((other) => {
          if (other !== this && !other.contains(this)) other.setOpen(false);
        });
      }

      this.toggle.setAttribute('aria-expanded', String(open));
      this.panel.hidden = !open;
    }
  }

  customElements.define('lagift-nav-disclosure', LagiftNavDisclosure);

  /* ==========================================================================
     <lagift-menu-drawer>
     ========================================================================== */

  class LagiftMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.lagift-menu-drawer__panel');
      this.opener = null;

      this.querySelector('[data-drawer-overlay]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.close());

      this.querySelectorAll('[data-drawer-open-panel]').forEach((button) => {
        button.addEventListener('click', () => this.showPanel(button.dataset.drawerOpenPanel));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      this.opener = opener;

      // Reset to the root list on the way in rather than on the way out, so a
      // sub-panel never visibly snaps back while the drawer is sliding away.
      this.showPanel('root', { focus: false });

      this.classList.add('is-open');
      opener?.setAttribute('aria-expanded', 'true');

      // Stop the page behind the drawer from scrolling.
      document.body.style.overflow = 'hidden';

      this.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;

      // One class. Visibility is delayed by the slide in CSS, so nothing is left
      // on screen waiting for a timer to hide it.
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      this.opener?.setAttribute('aria-expanded', 'false');
      this.opener?.focus({ preventScroll: true });
    }

    showPanel(name, { focus = true } = {}) {
      this.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.drawerPanel !== name;
      });

      if (focus && name !== 'root') {
        this.querySelector(`[data-drawer-panel="${name}"] ${FOCUSABLE}`)?.focus({ preventScroll: true });
      }
    }

    trapFocus(event) {
      const focusable = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('lagift-menu-drawer', LagiftMenuDrawer);
})();
